import { Button } from '@1trading/react/components/ui/button';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { Skeleton } from '@1trading/react/components/ui/skeleton';
import { FileText, Trash2, Upload } from 'lucide-react';
import { useRef } from 'react';
import { type FieldPathByValue, type FieldValues, type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDocument, useDocumentUploadMutation } from '@/hooks/queries/use-documents';
import { DOCUMENT_FILE_TYPES_BY_SOURCE, DOCUMENT_MAX_SIZE_BYTES, type DocumentSource, getDocumentExtension } from '@/services/api/documents';

const DOCUMENT_MAX_SIZE_MB = DOCUMENT_MAX_SIZE_BYTES / 1024 / 1024;

const formatDocumentSize = (sizeBytes: number) => (sizeBytes < 1024 * 1024 ? `${Math.ceil(sizeBytes / 1024)} KB` : `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`);

const InstitutionFieldDocumentSummary = ({ documentId }: { documentId: string }) => {
  const { isLoading, data: document } = useDocument(documentId);
  const { t } = useTranslation();

  if (isLoading) return <Skeleton className="h-5 w-48" />;

  return (
    <div className="min-w-0">
      <p className="truncate text-sm font-medium">{document?.filename ?? t('institutionDetails.document.uploaded')}</p>
      {document ? <p className="text-xs text-muted-foreground">{formatDocumentSize(document.sizeBytes)}</p> : null}
    </div>
  );
};
InstitutionFieldDocumentSummary.displayName = 'InstitutionFieldDocumentSummary';

type InstitutionFieldDocumentProps<T extends FieldValues> = {
  description?: string;
  label: string;
  name: FieldPathByValue<T, string | undefined>;
  source: DocumentSource;
};

const InstitutionFieldDocument = <T extends FieldValues>({ description, label, name, source }: InstitutionFieldDocumentProps<T>) => {
  const form = useFormContext<T>();
  const { isPending, mutateAsync } = useDocumentUploadMutation();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const fileTypes = DOCUMENT_FILE_TYPES_BY_SOURCE[source];
  const extensions = Object.keys(fileTypes);
  const fileTypesLabel = extensions.map((extension) => extension.toUpperCase()).join(', ');

  const uploadFile = async (file: File, onChange: (documentId: string | undefined) => void) => {
    if (!fileTypes[getDocumentExtension(file.name)]) {
      form.setError(name as Path<T>, { message: t('institutionDetails.document.errors.fileType', { types: fileTypesLabel }) });

      return;
    }

    if (!file.size || file.size > DOCUMENT_MAX_SIZE_BYTES) {
      form.setError(name as Path<T>, { message: t('institutionDetails.document.errors.fileSize', { size: DOCUMENT_MAX_SIZE_MB }) });

      return;
    }

    form.clearErrors(name as Path<T>);

    try {
      onChange(await mutateAsync({ file, source }));
    } catch {
      form.setError(name as Path<T>, { message: t('institutionDetails.document.errors.upload') });
    }
  };

  return (
    <FormField
      control={form.control}
      name={name as Path<T>}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}*</FormLabel>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FormControl>
            <input
              ref={inputRef}
              accept={extensions.map((extension) => `.${extension}`).join(',')}
              className="sr-only"
              disabled={isPending}
              type="file"
              onChange={(event) => {
                const file = event.target.files?.[0];

                // Clearing lets the same file be chosen again after an error
                event.target.value = '';

                if (file) uploadFile(file, field.onChange);
              }}
            />
          </FormControl>
          <div className="flex items-center gap-3 rounded-md border p-4">
            <FileText className="size-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              {isPending ? <p className="text-sm">{t('institutionDetails.document.uploading')}</p> : null}
              {!isPending && field.value ? <InstitutionFieldDocumentSummary documentId={field.value} /> : null}
              {!isPending && !field.value ? (
                <p className="text-sm text-muted-foreground">{t('institutionDetails.document.fileTypes', { types: fileTypesLabel, size: DOCUMENT_MAX_SIZE_MB })}</p>
              ) : null}
            </div>
            {field.value && !isPending ? (
              <Button className="shrink-0 text-destructive hover:text-destructive" size="icon" type="button" variant="ghost" onClick={() => field.onChange(undefined)}>
                <Trash2 className="size-4" />
                <span className="sr-only">{t('institutionDetails.document.remove')}</span>
              </Button>
            ) : null}
            <Button className="relative shrink-0 gap-2" disabled={isPending} size="sm" type="button" variant="outline" onClick={() => inputRef.current?.click()}>
              <span className={isPending ? 'invisible flex items-center gap-2' : 'flex items-center gap-2'}>
                <Upload className="size-4" />
                {field.value ? t('institutionDetails.document.replace') : t('institutionDetails.document.upload')}
              </span>
              {isPending ? <Loading position="overlay" size="sm" /> : null}
            </Button>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
InstitutionFieldDocument.displayName = 'InstitutionFieldDocument';

export { InstitutionFieldDocument };
